import {gql} from '@apollo/client';
import {fillArray} from '@benchling/js-utils';
import {useRef} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  ProcedureEdgeNode,
  ProcedureParameterConfirmationMethod,
  ProcedureType,
  PublishingStatus,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import {ProcedureParameterSchemaFieldName} from 'notebook/integrations/procedures/ParameterStructuredTableConstants';
import {
  convertTagToTypedValue,
  flattenBioentityLinkSchemaFieldTypedValues,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  BioentityLinkSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {sortNodesByCoordinates} from 'procedures/design/flowchartManagementUtils';
import isProcedureVersionPoW from 'procedures/design/utils/isProcedureVersionPoW';
import {
  ExperimentalInputs,
  ExperimentalInputTypedValuesByComponentName,
} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {getInitialTag} from 'schemas/tags/TagUtil';
import type {ExperimentalInputsProcedureFlowchartVersionGraphQL_ProcedureFlowchartVersionNode_largestTableTypeSizes_LargestTableTypeSizeNode as LargestTableSizeNode} from 'studies/typed/shared/__generated/ExperimentalInputsProcedureFlowchartVersionGraphQL';
import {getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

import {
  ExperimentalInputsFlowchartGraphQL_ProcedureFlowchartVersionNode_nodes_ProcedureMethodDefinitionVersionNode as ProcedureMethodDefinitionVersionNode,
  ExperimentalInputsFlowchartGraphQL_ProcedureFlowchartVersionNode_nodes_ProcedureMethodDefinitionVersionNode_dataParameterSettings_ProcedureDataParameterSettingNode as ProcedureDataParameterSettingNode,
  ExperimentalInputsFlowchartGraphQL_ProcedureFlowchartVersionNode_nodes_ProcedureMethodDefinitionVersionNode_equipmentSettings_ProcedureEquipmentSettingNode as ProcedureEquipmentSettingNode,
  ExperimentalInputsFlowchartGraphQL_ProcedureFlowchartVersionNode_nodes_ProcedureMethodDefinitionVersionNode_materialInputSettings_ProcedureMaterialInputSettingNode as ProcedureMaterialInputSettingNode,
} from './__generated/ExperimentalInputsFlowchartGraphQL';
import {
  ExperimentalInputsProcedureQuery,
  ExperimentalInputsProcedureQueryVariables,
  ExperimentalInputsProcedureQuery_procedure_ProcedureNode_procedureFlowchart_ProcedureFlowchartNode_publishedFlowchartVersion_ProcedureFlowchartVersionNode as ProcedureFlowchartVersion,
  ExperimentalInputsProcedureQuery_procedure_ProcedureNode_currentPublishingRecord as CurrentPublishingRecordOrInaccessibleInfo,
} from './__generated/ExperimentalInputsProcedureQuery';
import {
  ExperimentalInputsProcedureQueryWithFlowchartVersion,
  ExperimentalInputsProcedureQueryWithFlowchartVersionVariables,
  ExperimentalInputsProcedureQueryWithFlowchartVersion_procedure_ProcedureNode_publishingRecords as PublishingRecordOrInaccessibleInfo,
} from './__generated/ExperimentalInputsProcedureQueryWithFlowchartVersion';

export type ProcedureMethodDefinitionVersionNodeParameters = Pick<
  ProcedureMethodDefinitionVersionNode,
  'dataParameterSettings' | 'materialInputSettings' | 'equipmentSettings'
>;

function convertParameterSettingNodeToTypedValue(
  parameterNode: ProcedureDataParameterSettingNode
): [TypedValue] {
  const parameters: Array<TypedValue> = parameterNode.values.map((v) => v?.value);
  let parameterTypedValue: TypedValue | undefined;
  switch (parameterNode.version.confirmationMethod) {
    case ProcedureParameterConfirmationMethod.PLATE_MAP: {
      if (parameters.length > 1) {
        parameterTypedValue = {type: BaseTypedValueType.ARRAY, rawValue: parameters};
      } else {
        parameterTypedValue = parameters[0];
      }
      break;
    }
    case ProcedureParameterConfirmationMethod.TABLE: {
      assert(parameters.length <= 1, 'Expected at most one parameter value for table confirmed parameter', {
        parameters,
      });
      parameterTypedValue = parameters[0];
      break;
    }
    default: {
      assert(
        false,
        `Unexpected confirmationMethod: ${assertNever(parameterNode.version.confirmationMethod)}`
      );
    }
  }
  return [parameterTypedValue ?? convertTagToTypedValue(getInitialTag(parameterNode.version.restValueField))];
}

function convertMaterialInputSettingNodeToTypedValues(
  materialInputNode: ProcedureMaterialInputSettingNode
): [TypedValue, TypedValue] {
  const materials: Array<TypedValue | null> = materialInputNode.values.map((v) => v.material);
  const amounts: Array<FloatSchemaFieldTypedValue | null> = materialInputNode.values.map((v) => v.amount);

  switch (materialInputNode.version.confirmationMethod) {
    case ProcedureParameterConfirmationMethod.PLATE_MAP: {
      // Plate map supports multi-valued materials, but the amount must be the same for all materials.
      assert(
        materials.length <= 1 ||
          materials.every(
            (material): material is BioentityLinkSchemaFieldTypedValue =>
              material?.type === FieldType.BIOENTITY_LINK
          ),
        'Expected every material in a multi-value material to be a bioentity link typed value',
        {materials}
      );
      assert(new Set(amounts.map((amount) => amount?.value)).size <= 1, 'Expected every amount to be equal', {
        amounts,
      });
      const materialTypedValue =
        materials.length > 1
          ? flattenBioentityLinkSchemaFieldTypedValues(materials as Array<BioentityLinkSchemaFieldTypedValue>)
          : (materials[0] ??
            convertTagToTypedValue(getInitialTag(materialInputNode.version.restMaterialField)));
      const amountTypedValue =
        amounts[0] ?? convertTagToTypedValue(getInitialTag(materialInputNode.version.restAmountField));
      return [materialTypedValue, amountTypedValue];
    }
    case ProcedureParameterConfirmationMethod.TABLE: {
      // For non-plate confirmation, we only support up to one material/amount.
      assert(materialInputNode.values.length <= 1, 'Expected at most one material input value', {
        values: materialInputNode.values,
      });
      const materialTypedValue =
        materials[0] ?? convertTagToTypedValue(getInitialTag(materialInputNode.version.restMaterialField));
      const amountTypedValue =
        amounts[0] ?? convertTagToTypedValue(getInitialTag(materialInputNode.version.restAmountField));
      return [materialTypedValue, amountTypedValue];
    }
    default:
      assert(
        false,
        `Unexpected confirmationMethod: ${assertNever(materialInputNode.version.confirmationMethod)}`
      );
  }
}

/**
 * ProcedureDataParameterSetting.values and ProcedureMaterialInputSetting.values are arrays to support
 * multi-value parameters. However, for equipment, we only support single-value parameters so we should
 * use the first element of the equipment values array.
 */
const EQUIPMENT_SETPOINT_INDEX = 0;

function convertEquipmentSettingNodeToTypedValue(equipmentNode: ProcedureEquipmentSettingNode): [TypedValue] {
  return [
    equipmentNode.values[EQUIPMENT_SETPOINT_INDEX]?.equipment ??
      convertTagToTypedValue(getInitialTag(equipmentNode.version.restEquipmentField)),
  ];
}

export function getValuesForMethod(
  method: ProcedureMethodDefinitionVersionNodeParameters
): ExperimentalInputTypedValuesByComponentName {
  return {
    parameters: method.dataParameterSettings.map((p) => convertParameterSettingNodeToTypedValue(p)),
    materialInputs: method.materialInputSettings.map((m) => convertMaterialInputSettingNodeToTypedValues(m)),
    equipment: method.equipmentSettings.map((e) => convertEquipmentSettingNodeToTypedValue(e)),
  };
}

export type LargestTableTypeSizes = ProcedureFlowchartVersion['largestTableTypeSizes'];

export interface ExperimentFlowchart {
  id: string;
  nodes: Array<ProcedureMethodDefinitionVersionNode>;
  edges: Array<ProcedureEdgeNode>;
}

export type ExperimentMethod = ExperimentFlowchart['nodes'][number];

export interface ExperimentProcedure {
  id: string;
  procedureType: ProcedureType;
  procedureFlowchart: ExperimentFlowchart;
  procedureFlowchartVersionId: string | null;
  methods: Array<ExperimentMethod>;
  largestTableTypeSizes: LargestTableTypeSizes;
  workflowFlowchartConfigVersionId: string | null;
}

/**
 * Given a procedure (most likely, an ExperimentProcedure) with [workflowFlowchartVersion, procedureFlowchartVersion],
 * we return the correct version ID, and a flag to indicate if the procedure is PoW.
 *
 * PoW procedures return a workflow flowchart config version; legacy procedures return a procedure flowchart vesrion.
 */
export function selectFlowchartVersion({
  workflowFlowchartConfigVersionId,
  procedureFlowchartVersionId,
}: Pick<ExperimentProcedure, 'workflowFlowchartConfigVersionId' | 'procedureFlowchartVersionId'>): {
  versionId: string;
  isPoW: boolean;
} {
  const isPoW = workflowFlowchartConfigVersionId != null;
  assert(isPoW || procedureFlowchartVersionId != null, 'Unable to determine which flowchart version to use', {
    workflowFlowchartConfigVersionId,
    procedureFlowchartVersionId,
  });
  return {
    isPoW,
    versionId: isPoW ? workflowFlowchartConfigVersionId : procedureFlowchartVersionId!,
  };
}

/**
 * Massage ExperimentMethod into ExperimentalInputs.
 *
 * @param methods should be queried using EXPERIMENTAL_INPUTS_PROCEDURE_QUERY.
 *
 * @param prevExperimentalInputValues is a superset of a `ExperimentalInputs`, so that can directly be used here.
 */
export function prepareInitialExperimentalInputs(
  numConditions: number,
  methods: Array<ExperimentMethod>,
  {
    prevExperimentalInputValues,
    prevConfirmationMethods,
  }: {
    prevExperimentalInputValues?: {[methodId: string]: Pick<ExperimentalInputs[string], 'values'>};
    prevConfirmationMethods?: {
      [methodId: string]: Pick<ExperimentalInputs[string], 'parameterConfirmationMethods'>;
    };
  } = {}
): ExperimentalInputs {
  const experimentalInputs: ExperimentalInputs = {};
  for (const method of methods) {
    const defaultValues = getValuesForMethod(method);
    experimentalInputs[method.id] = {
      values: fillArray(
        numConditions,
        (conditionIdx) => prevExperimentalInputValues?.[method.id]?.values?.[conditionIdx] ?? defaultValues
      ),
      parameterNames: {
        parameters: method.dataParameterSettings.map((p) => p.version.name),
        materialInputs: method.materialInputSettings.map((m) => m.version.name),
        equipment: method.equipmentSettings.map((e) => e.version.name),
      },
      parameterConfirmationMethods: prevConfirmationMethods?.[method.id].parameterConfirmationMethods ?? {
        parameters: method.dataParameterSettings.map((p) => p.version.confirmationMethod),
        materialInputs: method.materialInputSettings.map((m) => m.version.confirmationMethod),
        equipment: method.equipmentSettings.map((m) => m.version.confirmationMethod),
      },
      defaultValues,
      ids: {
        parameters: method.dataParameterSettings.map((p) => p.version.id),
        materialInputs: method.materialInputSettings.map((m) => m.version.id),
        equipment: method.equipmentSettings.map((e) => e.version.id),
      },
      units: {parameters: [], materialInputs: [], equipment: []},
      fields: {
        parameters: method.dataParameterSettings.map((p) => [
          {
            schemaField: p.version.restValueField,
            displayName: 'Value',
            type: ProcedureParameterSchemaFieldName.VALUE_FIELD_NAME,
            maximum: p.version.maximum,
            minimum: p.version.minimum,
            allowMultiValueForPlateMap: true,
          },
        ]),
        materialInputs: method.materialInputSettings.map((m) => [
          {
            schemaField: m.version.restMaterialField,
            displayName: 'Default material',
            type: ProcedureParameterSchemaFieldName.MATERIAL_FIELD_NAME,
            allowMultiValueForPlateMap: true,
          },
          {
            schemaField: m.version.restAmountField,
            displayName: 'Amount',
            type: ProcedureParameterSchemaFieldName.AMOUNT_FIELD_NAME,
            allowMultiValueForPlateMap: false,
          },
        ]),
        equipment: method.equipmentSettings.map((e) => [
          {
            schemaField: e.version.restEquipmentField,
            displayName: 'Value',
            type: ProcedureParameterSchemaFieldName.EQUIPMENT_FIELD_NAME,
            allowedEquipmentModels: e.version.allowedEquipmentModels,
            allowMultiValueForPlateMap: false,
          },
        ]),
      },
    };
    assert(
      experimentalInputs[method.id].fields.parameters.length ===
        experimentalInputs[method.id].defaultValues.parameters.length,
      'Parameter array lengths do not match'
    );
    assert(
      experimentalInputs[method.id].fields.materialInputs.length ===
        experimentalInputs[method.id].defaultValues.materialInputs.length,
      'Material input array lengths do not match'
    );
    assert(
      experimentalInputs[method.id].fields.equipment.length ===
        experimentalInputs[method.id].defaultValues.equipment.length,
      'Equipment array lengths do not match'
    );
  }
  return experimentalInputs;
}

function isRecordPublished(
  record: CurrentPublishingRecordOrInaccessibleInfo | null,
  studyType?: StudyType
): boolean {
  if (record == null || isInaccessible(record)) {
    return false;
  }
  const isStudyTypeClinicalOrPreClinical =
    studyType === StudyType.CLINICAL || studyType === StudyType.PRECLINICAL;
  return (
    record.publishingStatus === PublishingStatus.EFFECTIVE ||
    (record.publishingStatus === PublishingStatus.SUPERSEDED &&
      isStudyTypeClinicalOrPreClinical &&
      BenchlingConfig.STUDIES_ENABLE_CLINICAL_PROCEDURE_PUBLISHING)
  );
}

type ProcedureWithPublishingData =
  | {
      id: string;
      procedureType: ProcedureType;
      publishingRecords: Array<PublishingRecordOrInaccessibleInfo>;
      currentPublishingRecord?: never;
    }
  | {
      id: string;
      procedureType: ProcedureType;
      currentPublishingRecord: CurrentPublishingRecordOrInaccessibleInfo | null;
      publishingRecords?: never;
    };

// This type will be satisfied by either a legacy version or a PoW version
interface ProcedureFlowchartVersionForParsing extends ExperimentFlowchart {
  largestTableTypeSizes: Array<LargestTableSizeNode>;
}

/**
 * @param procedure the procedure to parse.
 * @param procedureFlowchartVersion the flowchart version to use.
 * @return ExperimentProcedure or null if the procedure is not published.
 *
 * @package Exported for testing only
 */
export function parseProcedureForExperiment(
  procedureData: ProcedureWithPublishingData,
  procedureFlowchartVersion: ProcedureFlowchartVersionForParsing,
  procedureFlowchartConfigVersionProxy: ProcedureFlowchartVersionForParsing | null,
  studyType?: StudyType
): ExperimentProcedure | null {
  if (BenchlingConfig.PROCEDURES_ENABLE_PUBLISHING) {
    if (procedureData.publishingRecords != null) {
      // If publishing records are provided, use them.
      const matchingRecord = procedureData.publishingRecords.find(
        (record) => !isInaccessible(record) && record.publishableVersionId === procedureFlowchartVersion.id
      );
      const hasPublishedFlowchartVersion = isRecordPublished(matchingRecord ?? null, studyType);
      assert(hasPublishedFlowchartVersion, 'Expected procedure to be published.', {
        publishingRecords: procedureData.publishingRecords,
      });
    } else {
      // If no publishing records are provided, use the current publishing record.
      const hasPublishedFlowchartVersion = isRecordPublished(
        procedureData.currentPublishingRecord,
        studyType
      );
      assert(hasPublishedFlowchartVersion, 'Expected procedure to be published.', {
        currentPublishingRecord: procedureData.currentPublishingRecord,
      });
    }
  }

  return {
    id: procedureData.id,
    procedureType: procedureData.procedureType,
    procedureFlowchart: procedureFlowchartVersion,
    // When using the proxy for parsing (PoW), set procedureFlowchartVersionId to null
    procedureFlowchartVersionId:
      procedureFlowchartVersion.id === procedureFlowchartConfigVersionProxy?.id
        ? null
        : procedureFlowchartVersion.id,
    methods: sortNodesByCoordinates([...procedureFlowchartVersion.nodes], 'gridLayoutCoordinates'),
    largestTableTypeSizes:
      procedureFlowchartConfigVersionProxy?.largestTableTypeSizes ??
      procedureFlowchartVersion.largestTableTypeSizes,
    workflowFlowchartConfigVersionId: procedureFlowchartConfigVersionProxy?.id ?? null,
  };
}

export const EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT = gql`
  # nodes does not query id
  # eslint-disable benchlint/id-field-required
  fragment ExperimentalInputsFlowchartGraphQL on ProcedureFlowchartVersionNode {
    ...ProcedureFlowchartVersionGraphQL
    id
    nodes {
      dataParameterSettings {
        values {
          id
          value
        }
      }
      equipmentSettings {
        values {
          id
          equipment
        }
      }
      materialInputSettings {
        values {
          id
          material
          amount
        }
      }
    }
  }
  ${getFragmentWrapper('ProcedureFlowchartVersion').fragment}
`;

// largestTableTypeSizes is an expensive field to fetch, so we should only fetch it once
const EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_FRAGMENT = gql`
  fragment ExperimentalInputsProcedureFlowchartVersionGraphQL on ProcedureFlowchartVersionNode {
    id
    ...ExperimentalInputsFlowchartGraphQL
    largestTableTypeSizes {
      tableKind
      size
    }
  }
  ${EXPERIMENTAL_INPUTS_FLOWCHART_FRAGMENT}
`;

export const EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_PROXY_FRAGMENT = gql`
  fragment ExperimentalInputsProcedureFlowchartVersionProxyGraphQL on ProcedureFlowchartConfigVersionProxyNode {
    id
    ...ProcedureFlowchartConfigVersionProxyExperimentalInputsGraphQL
    largestTableTypeSizes {
      tableKind
      size
    }
  }
  ${getFragmentWrapper('ProcedureFlowchartConfigVersionProxyExperimentalInputs').fragment}
`;

export const EXPERIMENTAL_INPUTS_PROCEDURE_QUERY = gql`
  query ExperimentalInputsProcedureQuery($procedureId: StrictString!)
  @skipAudit
  @engComponent(name: STUDIES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        id
        procedureType
        procedureFlowchart {
          ... on ProcedureFlowchartNode {
            id
            publishedFlowchartVersion {
              ...ExperimentalInputsProcedureFlowchartVersionGraphQL
            }
          }
        }
        publishedProcedureFlowchartConfigVersionProxy {
          ...ExperimentalInputsProcedureFlowchartVersionProxyGraphQL
        }
        currentPublishingRecord {
          ... on PublishingRecordNode {
            id
            publishingStatus
          }
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_FRAGMENT}
  ${EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_PROXY_FRAGMENT}
`;

export const EXPERIMENTAL_INPUTS_PROCEDURE_QUERY_WITH_FLOWCHART_VERSION = gql`
  query ExperimentalInputsProcedureQueryWithFlowchartVersion(
    $procedureId: StrictString!
    $procedureFlowchartVersionId: StrictString!
  ) @skipAudit @engComponent(name: STUDIES) {
    procedure(id: $procedureId) {
      ... on ProcedureNode {
        id
        procedureType
        publishingRecords {
          ... on PublishingRecordNode {
            id
            publishingStatus
            publishableVersionId
          }
          ...InaccessibleInfoGraphQL
        }
      }
    }
    procedureFlowchartVersion(id: $procedureFlowchartVersionId) {
      ... on ProcedureFlowchartVersionNode {
        ...ExperimentalInputsProcedureFlowchartVersionGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('BaseProcedureMethodDefinitionVersion').fragment}
  ${EXPERIMENTAL_INPUTS_PROCEDURE_FLOWCHART_VERSION_FRAGMENT}
`;

export enum UseExperimentProcedureStatus {
  SUCCESS = 'SUCCESS',
  NOT_PROVIDED = 'NOT_PROVIDED',
  LOADING = 'LOADING',
  NOT_PUBLISHED = 'NOT_PUBLISHED',
}

type UseExperimentProcedureResult =
  | {status: UseExperimentProcedureStatus.SUCCESS; procedure: ExperimentProcedure}
  | {
      status:
        | UseExperimentProcedureStatus.NOT_PROVIDED
        | UseExperimentProcedureStatus.LOADING
        | UseExperimentProcedureStatus.NOT_PUBLISHED;
      procedure: null;
    };

function createProcedureResult(parsedProcedure: ExperimentProcedure | null): UseExperimentProcedureResult {
  return parsedProcedure != null
    ? {procedure: parsedProcedure, status: UseExperimentProcedureStatus.SUCCESS}
    : {procedure: null, status: UseExperimentProcedureStatus.NOT_PUBLISHED};
}

function getProcedureString(procedureType?: ProcedureType): string {
  return procedureType
    ? getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {stringCase: StringCase.LOWER})
    : 'procedure';
}

function getInaccessibleProcedureMessage(isProcedure: boolean, procedureType?: ProcedureType): string {
  const procedureString = getProcedureString(procedureType);
  return isProcedure
    ? `The ${procedureString} you were trying to access is no longer available. It may have been deleted or moved into a template collection you do not have access to.`
    : `The ${procedureString} version you were trying to access is no longer available. It may have been deleted or moved into a template collection you do not have access to.`;
}

/**
 * Hook to manage fetching the necessary information for designing an experiment from a procedure.
 *
 * @param experimentProcedureParams the id of the procedure to fetch and the optional id of the flowchart version to fetch.
 *  If `procedureFlowchartVersionId` is provided, the flowchart version and procedure will be fetched. The fetched
 *  flowchart version will be used to create the {@link ExperimentProcedure}.
 *  If `procedureFlowchartVersionId` is not provided, only the procedure will be fetched. The `publishedFlowchartVersion`
 *  of the procedure will be used to create the {@link ExperimentProcedure}.
 * @returns a {@link ExperimentProcedure} for the given `procedureId` and `procedureFlowchartVersionId`, or
 *  `null` (with a reason) if the status is anything other than SUCCESS
 *
 */
export function useExperimentProcedure(
  experimentProcedureParams: {
    procedureId: string;
    procedureFlowchartVersionId?: string;
  } | null,
  studyType?: StudyType
): UseExperimentProcedureResult {
  const {procedureId: procedureIdOrNull, procedureFlowchartVersionId: procedureFlowchartVersionIdOrNull} =
    experimentProcedureParams ?? {};
  const shouldFetchFlowchartVersion = procedureFlowchartVersionIdOrNull != null;
  const whichQueryToRun: 'Procedure' | 'Version' = shouldFetchFlowchartVersion ? 'Version' : 'Procedure';
  const procedureResult = useSkippableBenchlingQuery<
    ExperimentalInputsProcedureQuery,
    ExperimentalInputsProcedureQueryVariables
  >(EXPERIMENTAL_INPUTS_PROCEDURE_QUERY, {
    skip: procedureIdOrNull == null || whichQueryToRun === 'Version',
    variables: {procedureId: procedureIdOrNull!},
    // Always refetch in case the procedure has been updated since last use of this modal.
    fetchPolicy: 'network-only',
  });
  const procedureWithFlowchartVersionResult = useSkippableBenchlingQuery<
    ExperimentalInputsProcedureQueryWithFlowchartVersion,
    ExperimentalInputsProcedureQueryWithFlowchartVersionVariables
  >(EXPERIMENTAL_INPUTS_PROCEDURE_QUERY_WITH_FLOWCHART_VERSION, {
    skip:
      procedureIdOrNull == null ||
      procedureFlowchartVersionIdOrNull == null ||
      isProcedureVersionPoW(procedureFlowchartVersionIdOrNull) ||
      whichQueryToRun === 'Procedure',
    variables: {
      procedureId: procedureIdOrNull!,
      procedureFlowchartVersionId: procedureFlowchartVersionIdOrNull!,
    },
  });
  const responseRef = useRef<UseExperimentProcedureResult>();

  // Handle missing required inputs
  if (experimentProcedureParams == null) {
    responseRef.current = {procedure: null, status: UseExperimentProcedureStatus.NOT_PROVIDED};
    return responseRef.current;
  }

  const {procedureId, procedureFlowchartVersionId} = experimentProcedureParams;

  // Handle the case where we already have the procedure we need.
  if (
    responseRef.current?.procedure?.id === procedureId &&
    (whichQueryToRun === 'Procedure' ||
      responseRef.current.procedure.procedureFlowchartVersionId === procedureFlowchartVersionId)
  ) {
    return responseRef.current;
  }

  if (whichQueryToRun === 'Version' && procedureWithFlowchartVersionResult.type === QueryResultType.SUCCESS) {
    const {procedure, procedureFlowchartVersion} = procedureWithFlowchartVersionResult.data;
    if (isInaccessible(procedure)) {
      ToastActions.pushToast({
        type: ToastType.DANGER,
        message: getInaccessibleProcedureMessage(true, studyType && getProcedureTypeForStudyType(studyType)),
      });
      responseRef.current = {procedure: null, status: UseExperimentProcedureStatus.LOADING};
      return responseRef.current;
    }
    if (procedureFlowchartVersion && isInaccessible(procedureFlowchartVersion)) {
      ToastActions.pushToast({
        type: ToastType.DANGER,
        message: getInaccessibleProcedureMessage(false, procedure.procedureType),
      });
      responseRef.current = {procedure: null, status: UseExperimentProcedureStatus.LOADING};
      return responseRef.current;
    }

    const procedureOrNullIfUnpublished = parseProcedureForExperiment(
      procedure,
      procedureFlowchartVersion,
      null,
      studyType
    );
    responseRef.current = createProcedureResult(procedureOrNullIfUnpublished);
  } else if (whichQueryToRun === 'Procedure' && procedureResult.type === QueryResultType.SUCCESS) {
    const {procedure} = procedureResult.data;

    if (isInaccessible(procedure)) {
      ToastActions.pushToast({
        type: ToastType.DANGER,
        message: getInaccessibleProcedureMessage(true, studyType && getProcedureTypeForStudyType(studyType)),
      });
      responseRef.current = {procedure: null, status: UseExperimentProcedureStatus.LOADING};
      return responseRef.current;
    }
    const publishedFlowchartVersionProxy = procedure.publishedProcedureFlowchartConfigVersionProxy;
    if (publishedFlowchartVersionProxy != null && isInaccessible(publishedFlowchartVersionProxy)) {
      ToastActions.pushToast({
        type: ToastType.DANGER,
        message: getInaccessibleProcedureMessage(false, procedure.procedureType),
      });
      responseRef.current = {procedure: null, status: UseExperimentProcedureStatus.LOADING};
      return responseRef.current;
    }

    // For PoW procedures, use the proxy if available (which matches what the backend uses),
    // otherwise fall back to the legacy flowchart version.
    const legacyFlowchartVersion = procedure.procedureFlowchart?.publishedFlowchartVersion;
    const flowchartVersionForParsing = publishedFlowchartVersionProxy ?? legacyFlowchartVersion;

    if (flowchartVersionForParsing == null) {
      ToastActions.pushToast({
        type: ToastType.WARNING,
        message: `This ${getProcedureString(procedure.procedureType)} does not have a published version available. Please try again later.`,
      });
      logging.warn(`Procedure ${procedureId} is missing a published flowchart version`);
      responseRef.current = {procedure: null, status: UseExperimentProcedureStatus.NOT_PUBLISHED};
      return responseRef.current;
    }

    const procedureOrNullIfUnpublished = parseProcedureForExperiment(
      procedure,
      flowchartVersionForParsing,
      publishedFlowchartVersionProxy,
      studyType
    );
    responseRef.current = createProcedureResult(procedureOrNullIfUnpublished);
  } else {
    responseRef.current = {procedure: null, status: UseExperimentProcedureStatus.LOADING};
  }
  return responseRef.current;
}
